import {
  Button,
  GooeyPopover,
  GooeyPopoverContent,
  GooeyPopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TopbarButton
} from '@parsleyai/ui'
import { X } from 'lucide-react'
import { type MouseEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIsMobile } from '@/lib/useIsMobile'
import { useFeedbackNudge } from '@/store'
import FeedbackDialog from './FeedbackDialog'
import { FeedbackMark } from './FeedbackMark'

// The nudge waits out any open dialog or sheet; it would otherwise land underneath it, unseen.
const OPEN_MODAL_SELECTOR = '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
const MODAL_RECHECK_MS = 1000

/**
 * The always-available feedback trigger: a header button that owns its own `FeedbackDialog`, so a
 * header just drops in `<FeedbackTrigger />`. `compact` is the mobile bar's icon-only form. It also hosts
 * the one-time feedback nudge, shown only from whichever of the two header mounts is on screen.
 */
export default function FeedbackTrigger({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const nudge = useFeedbackNudge()
  const isVisibleMount = compact === useIsMobile()
  const nudgeOpen = isVisibleMount && nudge.open
  const { due, show } = nudge

  useEffect(() => {
    if (!isVisibleMount || !due || open) return
    const tryShow = () => {
      if (document.querySelector(OPEN_MODAL_SELECTOR)) return false
      show()
      return true
    }
    if (tryShow()) return
    const interval = setInterval(() => {
      if (tryShow()) clearInterval(interval)
    }, MODAL_RECHECK_MS)
    return () => clearInterval(interval)
  }, [isVisibleMount, due, open, show])

  const openDialog = () => {
    nudge.acknowledge()
    setOpen(true)
  }

  // preventDefault stops GooeyPopoverTrigger's own toggle — a click always means the dialog.
  const handleTriggerClick = (event: MouseEvent) => {
    event.preventDefault()
    openDialog()
  }

  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip open={tooltipOpen && !nudgeOpen} onOpenChange={setTooltipOpen}>
          {/* No onOpenChange: Esc and outside taps are ignored, so the once-ever nudge can't vanish
              before it's read. Only its own buttons (or the icon) close it. */}
          <GooeyPopover open={nudgeOpen} manageFocus={false} side="bottom" align="end" sideOffset={11}>
            <GooeyPopoverTrigger>
              <TooltipTrigger asChild>
                <TopbarButton
                  onClick={handleTriggerClick}
                  aria-label={t('feedback.trigger.label')}
                  active={open || nudgeOpen}
                  icon={<FeedbackMark />}>
                  {compact ? null : t('feedback.trigger.short')}
                </TopbarButton>
              </TooltipTrigger>
            </GooeyPopoverTrigger>
            <GooeyPopoverContent aria-label={t('feedback.nudge.dialogLabel')} className="w-[264px]">
              <div className="mb-1 flex items-start justify-between gap-3">
                <p className="pt-0.5 text-base font-semibold leading-snug tracking-[-0.01em] text-foreground">
                  {t('feedback.nudge.title')}
                </p>
                <button
                  type="button"
                  onClick={nudge.acknowledge}
                  aria-label={t('feedback.nudge.close')}
                  className="-mr-1 -mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-50/80 hover:text-foreground dark:hover:bg-gray-800/50">
                  <X className="h-[15px] w-[15px]" />
                </button>
              </div>
              <p className="mb-3 text-sm leading-snug text-muted-foreground">{t('feedback.nudge.body')}</p>
              <div className="flex gap-2">
                <Button variant="subtle" className="flex-1" onClick={openDialog}>
                  {t('feedback.nudge.cta')}
                </Button>
                <Button variant="ghost" onClick={nudge.acknowledge}>
                  {t('feedback.nudge.dismiss')}
                </Button>
              </div>
            </GooeyPopoverContent>
          </GooeyPopover>
          <TooltipContent
            side="bottom"
            className="flex flex-col gap-0.5 border-0 bg-brand-green-deep px-3.5 py-2 text-white dark:bg-[#223d1f]">
            <span className="text-sm font-semibold leading-none">{t('feedback.trigger.label')}</span>
            <span className="text-xs leading-none text-white/60">{t('feedback.trigger.tooltip')}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <FeedbackDialog open={open} onOpenChange={setOpen} />
    </>
  )
}
